import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BarChart3, Loader2 } from 'lucide-react'
import { DeckViewer } from '../deck/DeckViewer'
import { visibleFailedExhibits } from '../deck/deckPager'
import {
  ReportViewState,
  assertNeverViewState,
} from '@/features/studies/report/reportViewState'
import type { Deck } from '@digital-twins/deck-ui/slides/contracts'
import type { SampleDemographics } from '@digital-twins/shared-types'

interface DeckTabProps {
  deck?: Deck | null
  /** The report's deck view state (`reportViewState`). */
  viewState: ReportViewState
  /** The accepted slide sequence (item ids); places failed exhibits in the pager. */
  slideOrder?: Array<string>
  studyName?: string
  studyId?: string
  onDeckUpdate?: (deck: Deck) => void
  onLoadDeck?: () => Promise<boolean>
  canEdit?: boolean
  sampleDemographics?: SampleDemographics | null
  chartSegmentationEnabled?: boolean
  /** A public share-link view: no failure details, no Retry. */
  isPublicView?: boolean
  /** Retry offered on a failed slide (the page's report regeneration). */
  onRetryFailedExhibit?: () => void
  /** A retry is in flight. */
  isRetryingFailedExhibit?: boolean
  /** A sequence item the viewer opens at (a storyline exhibit link). */
  focusSlideId?: string | null
  /** Public-report share token. Owners leave this unset. */
  shareToken?: string
}

/** A reader-requested deck load, shown as a fetch while it runs. */
type DeckLoadRequest = 'idle' | 'loading'

export function DeckTab({
  deck,
  viewState,
  slideOrder,
  studyName,
  studyId,
  onDeckUpdate,
  onLoadDeck,
  canEdit = false,
  sampleDemographics = null,
  chartSegmentationEnabled = true,
  isPublicView = false,
  onRetryFailedExhibit,
  isRetryingFailedExhibit = false,
  focusSlideId = null,
  shareToken,
}: DeckTabProps) {
  const { t } = useTranslation('app')
  const [loadRequest, setLoadRequest] = useState<DeckLoadRequest>('idle')

  const handleLoadDeck = async () => {
    if (!onLoadDeck || loadRequest === 'loading') return
    setLoadRequest('loading')
    try {
      await onLoadDeck()
    } finally {
      setLoadRequest('idle')
    }
  }

  const state: ReportViewState =
    loadRequest === 'loading'
      ? { kind: 'deck_fetching', failedExhibits: viewState.failedExhibits }
      : viewState

  switch (state.kind) {
    case 'deck_fetching':
    case 'deck_generating': {
      // A new deck generating never hides the readable one: the
      // placeholder is only for a study with no readable deck yet.
      const showsGenerating = state.kind === 'deck_generating'
      return (
        <div
          className="flex-1 flex items-center justify-center"
          data-testid={showsGenerating ? 'deck-tab-generating' : 'deck-tab-loading'}
        >
          <div className="text-center max-w-md px-6">
            <div className="relative mb-6">
              <div
                className="absolute inset-0 rounded-full bg-primary/20 animate-ping"
                style={{ animationDuration: '2s' }}
              />
              <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-muted mx-auto">
                <Loader2 className="w-10 h-10 text-muted-foreground animate-spin" />
              </div>
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-2">
              {showsGenerating
                ? t('studyReport.deckGeneratingTitle')
                : t('studyReport.deckLoadingTitle')}
            </h3>
            <p className="text-muted-foreground">
              {showsGenerating
                ? t('studyReport.deckGeneratingBody')
                : t('studyReport.deckLoadingBody')}
            </p>
          </div>
        </div>
      )
    }
    case 'deck_ready':
      if (deck) {
        return (
          <div className="flex-1 min-h-0 overflow-hidden p-4">
            <DeckViewer
              deck={deck}
              experimentName={studyName}
              coverBackgroundSeed={studyId}
              studyId={studyId}
              sampleDemographics={sampleDemographics}
              chartSegmentationEnabled={chartSegmentationEnabled}
              shareToken={shareToken}
              // A deck regenerating behind this one would overwrite edits.
              editable={canEdit && !state.isUpdating}
              onDeckUpdate={onDeckUpdate}
              failedExhibits={visibleFailedExhibits(state.failedExhibits, { isPublicView })}
              slideOrder={slideOrder}
              isPublicView={isPublicView}
              onRetryFailedExhibit={onRetryFailedExhibit}
              isRetryingFailedExhibit={isRetryingFailedExhibit}
              focusSlideId={focusSlideId}
              embedded
              className="h-full"
            />
          </div>
        )
      }
      return <DeckTabEmpty onLoadDeck={onLoadDeck ? handleLoadDeck : undefined} />
    case 'deck_failed':
    case 'deck_skipped':
    case 'deck_absent':
      return <DeckTabEmpty onLoadDeck={onLoadDeck ? handleLoadDeck : undefined} />
    default:
      return assertNeverViewState(state)
  }
}

interface DeckTabEmptyProps {
  onLoadDeck?: () => void
}

function DeckTabEmpty(props: DeckTabEmptyProps) {
  const { onLoadDeck } = props

  const { t } = useTranslation('app')

  return (
    <div className="flex-1 flex items-center justify-center" data-testid="deck-tab-empty">
      <div className="text-center max-w-md px-6">
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-muted flex items-center justify-center">
          <BarChart3 className="w-10 h-10 text-muted-foreground" strokeWidth={1.5} />
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-2">
          {t('studyReport.deckEmptyTitle')}
        </h3>
        <p className="text-muted-foreground mb-4">
          {t('studyReport.deckEmptyBody')}
        </p>
        {onLoadDeck && (
          <button
            type="button"
            data-testid="deck-tab-load-button"
            onClick={onLoadDeck}
            className="px-4 py-2 rounded-full text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            {t('studyReport.deckLoadButton')}
          </button>
        )}
      </div>
    </div>
  )
}
